import React, {useState, useEffect} from 'react';
import axios from 'axios';
import {
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Switch
} from '@mui/material';
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import CustomAlert from "../../../components/ConfirmationModal/CustomAlert";
import SettingsEnum from "../../../enums/SettingsEnum";
import LoaderCustomFixed from "../../../components/ConfirmationModal/LoaderCustomFixed";

const SettingManager = () => {
    const initialKeyword = "DefaultCaseDays";
    const [settings, setSettings] = useState([]);
    const [selectedKeyword, setSelectedKeyword] = useState(initialKeyword);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentSettingId, setCurrentSettingId] = useState(null);
    const [editingSetting, setEditingSetting] = useState({});
    const [alert, setAlert] = useState({message: '', type: '', open: false});
    const [permitTypeTree, setPermitTypeTree] = useState([]);
    const [isLoading, setIsLoading] = useState(true);


    useEffect(() => {
        fetchSettings();
        fetchPermitTypeTree();
    }, [selectedKeyword]);

    const fetchSettings = () => {
        setIsLoading(true);
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/GetAllSetting`)
            .then(response => {
                if (response.data && response.data.success) {
                    setSettings(response.data.data);
                }
            })
            .catch(error => console.error('Error fetching settings:', error));
        setIsLoading(false);
    };


    const handleDropdownChange = (event) => {
        setSelectedKeyword(event.target.value);
    };


    const handleEditClick = (setting) => {
        setEditingSetting({...setting});
        setCurrentSettingId(setting.id);
    };

    const handleEditChange = (e, field) => {
        const { value, checked } = e.target;
        let newValue;

        if (field === 'isDeleted') {
            newValue = checked;
        } else if (field === 'value') {
            if (["SendMailForAdminRegistration",
                "SendMailForUserRegistration",
                "SendUpdateMailToRole",
                "DefaultBackFlowActivated",
                "TableColumnsStaticInputsOn",
                "RegistrationStaticInputsOn",
                "SeeGetAllCasePublic",
                "GetPhisicalPersonInfo",
                "Login",
                "GetSubjByIdNrQuery",
                "AddNewCase"
            ].includes(selectedKeyword)) {
                newValue = selectedKeyword === "DefaultBackFlowActivated" ? (checked ? "true" : "false") : (checked ? "1" : "0");
            } else {
                newValue = value;
            }
        } else if (field === 'additionalValue' && selectedKeyword === "AddCaseFile") {
            // Append "_MB" when the selectedKeyword is 'AddCaseFile'
            newValue = `${value}_MB`;
        } else {
            newValue = value;
        }

        setEditingSetting({ ...editingSetting, [field]: newValue });
    };

    const parseTimeToHours = (timeValue) => {
        if (!timeValue) return '';
        // Directly parse the hour part from the time string
        const hours = parseInt(timeValue.substring(11, 13), 10);
        return hours + 1; // Return the hour part
    };


    const fetchPermitTypeTree = () => {
        if (["DefaultCaseDays", "DefaultCaseRejectionDays", "DefaultCaseUserFirstRole", "DefaultUserDays", "TableColumnsStaticInputsOn", "RegistrationStaticInputsOn"].includes(selectedKeyword)) {
            axios.get(`${process.env.REACT_APP_API_BASE_URL}/GetPermitTypeTree`)
                .then(response => {
                    if (response.data && response.data.success) {
                        setPermitTypeTree(response.data.data);
                    }
                })
                .catch(error => console.error('Error fetching permit type tree:', error));
        }
    };
    const findPermitTypeTitle = (id, tree) => {
        let title = '';
        let parentTitle = '';

        const searchTree = (node, parent) => {
            if (node.id.toString() === id) {
                title = node.title;
                parentTitle = parent ? parent.title : '';
                return true;
            }
            if (node.childrenPermitTypes) {
                for (let child of node.childrenPermitTypes) {
                    if (searchTree(child, node)) {
                        return true;
                    }
                }
            }
            return false;
        };

        tree.forEach(node => searchTree(node, null));
        return parentTitle ? `${parentTitle}/${title}` : title;
    };


    const handleSaveClick = () => {
        setIsEditModalOpen(true);
    };

    const handleConfirmEdit = async () => {
        setIsEditModalOpen(false);
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/EditSetting`, editingSetting);
            if (response.data.success) {
                setAlert({message: 'ოპერაცია წარმატებით დასრულდა', type: 'success', open: true});
                fetchSettings();
            } else {
                setAlert({
                    message: 'დაფიქსრიდა შეცდომა, ცადეთ მოგვიანებით: ' + response.data.errors[0].message,
                    type: 'error',
                    open: true
                });
            }
        } catch (error) {
            setAlert({message: 'დაფიქსრიდა შეცდომა, ცადეთ მოგვიანებით' + error.message, type: 'error', open: true});
        } finally {
            setCurrentSettingId(null); // Exit edit mode
        }
    };

    const filteredSettings = settings.filter(setting => setting.keyWord === selectedKeyword);


    return (
        <div className="settings-manager-page">
            <FormControl fullWidth>
                <InputLabel id="keyword-select-label">პარამეტრი</InputLabel>
                <Select
                    labelId="keyword-select-label"
                    id="keyword-select"
                    value={selectedKeyword}
                    label="პარამეტრი"
                    onChange={handleDropdownChange}
                >
                    {Object.keys(SettingsEnum).map((key, index) => (<MenuItem key={index} value={key}>
                        {SettingsEnum[key]}
                    </MenuItem>))}
                </Select>
            </FormControl>

            <TableContainer component={Paper}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            {selectedKeyword !== "AddCaseFile" && (
                                <>
                                    {selectedKeyword === "CaseNumberFormat" ? (
                                        <TableCell align="right">ფორმატი</TableCell>
                                    ) : selectedKeyword === "DefaultCaseDays" || selectedKeyword === "DefaultCaseRejectionDays" ||
                                    selectedKeyword === "DefaultUserDays" ? (
                                            <TableCell align="right">დღეები</TableCell>
                                        ) :
                                        selectedKeyword === "SeeGetAllCasePublic" ? (
                                                <TableCell align="right">განაცხადების დამალვა</TableCell>
                                            ) :
                                            selectedKeyword === "SeeGetAllCasePublic" ||
                                            selectedKeyword === "GetSubjByIdNrQuery" ||
                                            selectedKeyword === "GetPhisicalPersonInfo" ||
                                            selectedKeyword === "AddNewCase" ||
                                            selectedKeyword === "Login"
                                                ? (
                                                    <TableCell align="right">ჩართულია</TableCell>
                                                ) :
                                                (
                                                    <TableCell align="right">პარამეტრი</TableCell>
                                                )}
                                </>
                            )}

                            {selectedKeyword === "Login" ||
                            selectedKeyword === "GetSubjByIdNrQuery" ||
                            selectedKeyword === "GetPhisicalPersonInfo" ||
                            selectedKeyword === "AddNewCase" ? (
                                <TableCell align="right">ცდების რაოდენობა</TableCell>
                            ) : (
                                <TableCell align="right">დამატებითი პარამეტრი</TableCell>
                            )}
                            {
                            selectedKeyword === "GetSubjByIdNrQuery" ||
                            selectedKeyword === "GetPhisicalPersonInfo" ||
                            selectedKeyword === "AddNewCase" ? (
                                <TableCell align="right">საათების რაოდენობა</TableCell>
                            ) : (
                                <></>
                            )}

                            <TableCell align="right">პარამეტრი</TableCell>
                            <TableCell align="right">ქმედება</TableCell>
                        </TableRow>
                    </TableHead>
                    {isLoading ? (<div style={{minHeight: 800, paddingTop: "25%", minWidth: 1098}}><LoaderCustomFixed/></div>) : (
                        <TableBody>
                            {filteredSettings.map((setting) => {
                                let displayValue = setting.additionalValue;

                                if (["DefaultCaseDays", "DefaultCaseRejectionDays", "DefaultCaseUserFirstRole", "DefaultUserDays", "RegistrationStaticInputsOn", "TableColumnsStaticInputsOn"].includes(selectedKeyword)) {
                                    const tempDisplayValue = findPermitTypeTitle(setting.additionalValue, permitTypeTree);
                                    displayValue = tempDisplayValue ? tempDisplayValue : "არაფერი მოიძებნა";
                                }

                                if (displayValue === "არაფერი მოიძებნა") {
                                    return null; // Skip rendering this row
                                }

                                return (
                                    <TableRow key={setting.id}>
                                        {selectedKeyword !== "AddCaseFile" && (
                                            <>
                                                <TableCell align="right">
                                                    {currentSettingId === setting.id ? (
                                                        (selectedKeyword === "DefaultBackFlowActivated") ? (
                                                            <Switch
                                                                checked={editingSetting.value === "true"}
                                                                onChange={(e) => handleEditChange(e, 'value')}
                                                                color="primary"
                                                            />
                                                        ) : (selectedKeyword === "SendMailForAdminRegistration" ||
                                                            selectedKeyword === "SeeGetAllCasePublic" ||
                                                            selectedKeyword === "SendMailForUserRegistration" ||
                                                            selectedKeyword === "SendUpdateMailToRole" ||
                                                            selectedKeyword === "RegistrationStaticInputsOn" ||
                                                            selectedKeyword === "SeeGetAllCasePublic" ||
                                                            selectedKeyword === "GetSubjByIdNrQuery" ||
                                                            selectedKeyword === "GetPhisicalPersonInfo" ||
                                                            selectedKeyword === "Login" ||
                                                            selectedKeyword === "AddNewCase" ||
                                                            selectedKeyword === "TableColumnsStaticInputsOn") ? (
                                                            <Switch
                                                                checked={editingSetting.value === "1"}
                                                                onChange={(e) => handleEditChange(e, 'value')}
                                                                color="primary"
                                                            />
                                                        ) : (
                                                            <TextField
                                                                value={editingSetting.value}
                                                                onChange={(e) => handleEditChange(e, 'value')}
                                                            />
                                                        )
                                                    ) : (
                                                        (selectedKeyword === "DefaultBackFlowActivated") ? (
                                                            setting.value === "true" ? 'ჩართულია' : 'გამორთულია'
                                                        ) : (selectedKeyword === "SendMailForAdminRegistration" ||
                                                            selectedKeyword === "SeeGetAllCasePublic" ||
                                                            selectedKeyword === "SendMailForUserRegistration" ||
                                                            selectedKeyword === "SendUpdateMailToRole" ||
                                                            selectedKeyword === "RegistrationStaticInputsOn" ||
                                                            selectedKeyword === "GetSubjByIdNrQuery" ||
                                                            selectedKeyword === "GetPhisicalPersonInfo" ||
                                                            selectedKeyword === "Login" ||
                                                            selectedKeyword === "AddNewCase" ||
                                                            selectedKeyword === "TableColumnsStaticInputsOn") ? (
                                                            setting.value === "1" ? 'ჩართულია' : 'გამორთულია'
                                                        ) : (
                                                            setting.value
                                                        )
                                                    )}
                                                </TableCell>
                                            </>
                                        )}



                                        <TableCell align="right">
                                            {selectedKeyword === "AddCaseFile" && currentSettingId === setting.id ? (


                                                    <TextField
                                                        type="number"
                                                        value={editingSetting.additionalValue.replace('_MB', '')} // Remove '_MB' when editing
                                                        onChange={(e) => handleEditChange(e, 'additionalValue')}
                                                        fullWidth
                                                    />
                                                )
                                                :
                                                (currentSettingId === setting.id) ? (
                                                    <TextField
                                                        type="number"
                                                        inputProps={{
                                                            inputMode: 'numeric',
                                                            pattern: '[0-9]*'
                                                        }} // Ensures only numbers can be entered
                                                        value={editingSetting.additionalValue || ''} // Use editingSetting.additionalValue
                                                        onChange={(e) => handleEditChange(e, 'additionalValue')} // Update the correct field
                                                        fullWidth
                                                    />
                                                ) : selectedKeyword === "AddCaseFile" ? (
                                                    displayValue.replace('_', ' ')
                                                ) : (
                                                    displayValue // Display the original display value when not in edit mode
                                                )}
                                        </TableCell>
                                        {
                                        selectedKeyword === "GetSubjByIdNrQuery" ||
                                        selectedKeyword === "GetPhisicalPersonInfo" ||
                                        selectedKeyword === "AddNewCase" ? (
                                            <TableCell align="right">
                                                {currentSettingId === setting.id ? (
                                                    <Select
                                                        value={editingSetting.timeValue ? parseTimeToHours(editingSetting.timeValue) : ''}
                                                        onChange={(e) => {
                                                            const hour = e.target.value;
                                                            const hourFormatted = (hour - 1).toString().padStart(2, '0'); // Adjust for zero-based index while formatting
                                                            const newTimeValue = `2024-10-07T${hourFormatted}:59:59Z`; // Construct the new time string with 'Z' for UTC
                                                            handleEditChange({target: {value: newTimeValue}}, 'timeValue'); // Update the 'timeValue' field
                                                        }}
                                                        displayEmpty
                                                    >
                                                        {Array.from({length: 24}, (_, i) => (
                                                            <MenuItem key={i} value={i + 1}> {/* Values from 1 to 24 */}
                                                                {i + 1} საათი
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    `${parseTimeToHours(setting.timeValue)} საათი`
                                                )}
                                            </TableCell>


                                        ) : (<></>)}


                                        <TableCell align="right">
                                            {currentSettingId === setting.id ? (SettingsEnum[setting.keyWord] || setting.keyWord // Fallback to keyWord if no match in SettingsEnum

                                            ) : (SettingsEnum[setting.keyWord] || setting.keyWord // Fallback to keyWord if no match in SettingsEnum
                                            )}
                                        </TableCell>


                                        <TableCell align="right">
                                            {currentSettingId === setting.id ? (
                                                <>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSaveClick}
                                                        style={{marginRight: '10px'}}
                                                    >
                                                        შენახვა
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => setCurrentSettingId(null)}
                                                    >
                                                        გაუქმება
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    onClick={() => handleEditClick(setting)}
                                                >
                                                    რედაქტირება
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>);
                            })}
                        </TableBody>
                    )}


                </Table>
            </TableContainer>


            <ConfirmationModal
                isOpen={isEditModalOpen}
                onRequestClose={() => setIsEditModalOpen(false)}
                onConfirm={handleConfirmEdit}

            />

            {alert.open && (<CustomAlert
                message={alert.message}
                type={alert.type}
                onClose={() => setAlert({...alert, open: false})}
            />)}
        </div>);
};

export default SettingManager;